import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faCheckCircle,
    faTimesCircle,
    faEdit,
    faTrash,
    faExclamationTriangle,
    faSyncAlt,
    faEllipsisVertical,
    faClock,
    faCircleCheck,
    faCircleXmark,
    faUndo
} from '@fortawesome/free-solid-svg-icons';
import { Typography, Chip } from '@material-tailwind/react';
import MobileExpenseCard from './MobileExpenseCard';
import { formatDate, formatCurrency } from '../utils';

const StatusIcon = ({ status, type }) => {
    const normalized = (status || '').toUpperCase();
    if (type === 'reimbursement' && !status) return <span className="text-xs text-gray-400">N/A</span>;

    let icon = faCircleCheck;
    let color = 'text-gray-400';
    let title = normalized || 'N/A';

    if (normalized === 'APPROVED') {
        icon = faCircleCheck;
        color = 'text-green-600';
        title = 'Approved';
    } else if (normalized === 'PENDING') {
        icon = faClock;
        color = 'text-amber-600';
        title = type === 'reimbursement' ? 'Reimbursement pending' : 'Pending approval';
    } else if (normalized === 'REJECTED') {
        icon = faCircleXmark;
        color = 'text-red-600';
        title = 'Rejected';
    } else if (normalized === 'PAID') {
        icon = faCircleCheck;
        color = 'text-green-600';
        title = 'Reimbursement paid';
    } else if (normalized === 'CANCELLED') {
        icon = faCircleXmark;
        color = 'text-red-600';
        title = 'Reimbursement cancelled';
    }

    return (
        <span title={title} aria-label={title} className="inline-flex items-center justify-center">
            <FontAwesomeIcon icon={icon} className={`text-lg ${color}`} />
        </span>
    );
};

const RowActionsMenu = ({ expense, onView, onEdit, onDelete, onApprove, onReject, onReverse, canEdit, canDelete, canApprove, canReject, canReverse }) => {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const canModerate = expense.approval_status === 'PENDING';

    return (
        <div ref={menuRef} className="relative inline-flex justify-end overflow-visible">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-[#FF5733]"
                title="More actions"
                aria-label={`More actions for ${expense.expense_no}`}
            >
                <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-44 rounded-none border border-gray-200 bg-white shadow-2xl z-[9999] overflow-hidden">
                    <button type="button" onClick={() => { setOpen(false); onView(expense); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <FontAwesomeIcon icon={faEye} className="text-blue-600" /> View
                    </button>
                    {canApprove && canReject && canModerate && (
                        <>
                            <button type="button" onClick={() => { setOpen(false); onApprove(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" /> Approve
                            </button>
                            <button type="button" onClick={() => { setOpen(false); onReject(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                <FontAwesomeIcon icon={faTimesCircle} className="text-red-600" /> Reject
                            </button>
                        </>
                    )}
                    {canEdit && (
                        <button type="button" onClick={() => { setOpen(false); onEdit(expense); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <FontAwesomeIcon icon={faEdit} className="text-amber-600" /> Edit
                        </button>
                    )}
                    {canDelete && !expense.is_reversed && (
                        <button type="button" onClick={() => { setOpen(false); onDelete(expense.expense_id); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <FontAwesomeIcon icon={faTrash} className="text-red-600" /> Delete
                        </button>
                    )}
                    {canReverse && expense.approval_status === 'APPROVED' && !expense.is_reversed && (
                        <button type="button" onClick={() => { setOpen(false); onReverse(expense.expense_id, expense.expense_no); }} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                            <FontAwesomeIcon icon={faUndo} className="text-orange-500" /> Reverse
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const ExpenseList = ({
    filteredExpenses,
    isMobile,
    loading,
    onView,
    onEdit,
    onDelete,
    onApprove,
    onReject,
    onReverse,
    canEdit = true,
    canDelete = true,
    canApprove = true,
    canReject = true,
    canReverse = false
}) => {
    if (loading) {
        return (
            <div className="text-center py-8">
                <FontAwesomeIcon icon={faSyncAlt} spin size="2x" className="text-[#FF5733]" />
                <Typography variant="small" className="mt-2 text-sm text-gray-600">Loading expenses...</Typography>
            </div>
        );
    }

    if (filteredExpenses.length === 0) {
        return (
            <div className="text-center py-8">
                <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="text-gray-400" />
                <Typography variant="small" className="mt-2 text-sm text-gray-600">No expenses found</Typography>
            </div>
        );
    }

    if (isMobile) {
        return (
            <div className="space-y-3">
                {filteredExpenses.map((expense, index) => (
                    <MobileExpenseCard
                        key={`expense-${expense.expense_id ?? expense.expense_no ?? index}`}
                        expense={expense}
                        onView={() => onView(expense)}
                        onEdit={() => onEdit(expense)}
                        onDelete={() => onDelete(expense.expense_id)}
                        onApprove={() => onApprove(expense.expense_id)}
                        onReject={() => onReject(expense.expense_id)}
                        onReverse={onReverse}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canApprove={canApprove}
                        canReject={canReject}
                        canReverse={canReverse}
                    />
                ))}
            </div>
        );
    }

    return (
        <table className="w-full min-w-[960px] table-auto text-left text-xs">
            <thead>
                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Expense No
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Date
                        </Typography>
                    </th>
                    <th className="p-2 text-center">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Type
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Category
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Description
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Payment Ref
                        </Typography>
                    </th>
                    <th className="p-2 text-right">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs text-right">
                            Amount
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Approval
                        </Typography>
                    </th>
                    <th className="p-2">
                        <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-xs">
                            Actions
                        </Typography>
                    </th>
                </tr>
            </thead>
            <tbody>
                {filteredExpenses.map((expense, index) => (
                    <tr key={`expense-${expense.expense_id ?? expense.expense_no ?? index}`} className="align-top border-b border-gray-100 hover:bg-[#fff8f5]">
                        <td className="p-2">
                            <span className="text-xs font-semibold text-slate-800">{expense.expense_no}</span>
                        </td>
                        <td className="p-2">
                            <span className="text-xs text-slate-600">{formatDate(expense.expense_date)}</span>
                        </td>
                        <td className="p-2 text-center">
                            <div className="flex justify-center">
                                <Chip value={expense.expense_type} color={expense.expense_type === 'VEHICLE' ? 'blue' : 'purple'} size="sm" />
                            </div>
                        </td>
                        <td className="p-2">
                            <span className="text-xs text-slate-600">
                                {expense.line_items_count > 1 ? `Multiple (${expense.line_items_count} items)` : (expense.category_name || '—')}
                                {expense.line_items_count <= 1 && expense.sub_category_name && (
                                    <span className="text-slate-400 text-xs block">{expense.sub_category_name}</span>
                                )}
                            </span>
                        </td>
                        <td className="p-2 max-w-[10rem]">
                            <span
                                className="text-xs text-slate-600 truncate block"
                                title={expense.plate_no ? `${expense.description || ''}\nVehicle: ${expense.plate_no}` : (expense.description || '')}
                            >
                                {expense.description}
                            </span>
                        </td>
                        <td className="p-2">
                            <span className="text-xs text-slate-600">{expense.payment_ref || '-'}</span>
                        </td>
                        <td className="p-2 text-right">
                            <span className="text-xs font-semibold text-slate-800">{formatCurrency(expense.amount)}</span>
                        </td>
                        <td className="p-2">
                            <div className="flex items-center gap-3 flex-wrap">
                                <StatusIcon status={expense.approval_status} />
                                {expense.requires_reimbursement && (
                                    <StatusIcon status={expense.reimbursement_status} type="reimbursement" />
                                )}
                                {expense.is_reversed && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-700">
                                        REVERSED
                                    </span>
                                )}
                            </div>
                        </td>
                        <td className="p-2 overflow-visible">
                            <RowActionsMenu
                                expense={expense}
                                onView={onView}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onApprove={onApprove}
                                onReject={onReject}
                                onReverse={onReverse}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                canApprove={canApprove}
                                canReject={canReject}
                                canReverse={canReverse}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default ExpenseList;
